import { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Mail, ShieldCheck, PoundSterling, ArrowLeft, ArrowRight, Zap, Users, Phone, GraduationCap, Recycle, ShieldPlus } from 'lucide-react';
import PublicFaq from '../../components/PublicFaq';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CaptureForm = ({ source, interest, cta, testid }) => {
  const [f, setF] = useState({ name: '', organisation: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.email.trim()) { toast.error('Email required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/leads`, { email: f.email.trim(), name: f.name.trim() || null, organisation: f.organisation.trim() || null, interest, source });
      setDone(true); toast.success("Thanks, we'll be in touch.");
    } catch { toast.error('Something went wrong. Please try again.'); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center" data-testid={`${testid}-success`}>
      <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
      <p className="font-semibold text-gray-900">Thanks, you're in.</p>
      <p className="text-sm text-gray-600 mt-1">We'll be in touch shortly.</p>
    </div>
  );

  const field = 'w-full px-4 py-3 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40';
  return (
    <form onSubmit={submit} className="space-y-3" data-testid={testid}>
      <input type="text" placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} data-testid={`${testid}-name`} />
      <input type="text" placeholder="Organisation" value={f.organisation} onChange={(e) => setF({ ...f, organisation: e.target.value })} className={field} data-testid={`${testid}-org`} />
      <input type="email" required placeholder="Work email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} data-testid={`${testid}-email`} />
      <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md transition-colors disabled:opacity-60" data-testid={`${testid}-submit`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} {cta}
      </button>
    </form>
  );
};

const BackToDirectory = () => (
  <Link to="/browse" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#00A79D] mb-6" data-testid="proc-back">
    <ArrowLeft className="w-4 h-4" /> Back to the directory
  </Link>
);

// Future, procurement funnel: better value on care insurance (capture-only)
export const InsuranceValue = () => (
  <div data-testid="page-insurance-value">
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <BackToDirectory />
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-5"><PoundSterling className="w-3.5 h-3.5" /> Procurement</span>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-extrabold text-gray-900 leading-tight">Get genuinely good value on your care insurance.</h1>
          <p className="text-lg text-gray-600 mt-5">Insurance is one of the biggest line-items for a care service, and one of the hardest to compare fairly. Tell us your setup and we'll help you benchmark cover and cost with trusted specialists.</p>
          <ul className="mt-6 space-y-2">
            {['An independent view of what good looks like', 'A fair comparison across specialist care insurers', 'No obligation, no hard sell'].map((x) => (
              <li key={x} className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-4 h-4 text-[#00A79D]" /> {x}</li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 lg:sticky lg:top-24">
          <h2 className="font-heading text-xl font-semibold text-gray-900 mb-1">Request a review</h2>
          <p className="text-sm text-gray-500 mb-4">We'll be in touch to understand your needs.</p>
          <CaptureForm source="procurement_insurance" interest="care insurance value" cta="Request a review" testid="insurance-lead" />
          <Link to="/procurement/insurance/report" className="mt-4 flex items-center justify-center gap-1.5 text-sm font-semibold text-[#00A79D] hover:text-[#00857D]" data-testid="insurance-report-cta">
            Or get a personalised report (£49) <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  </div>
);

// Future, supplier assurance reviews / reports (capture-only)
export const AssuranceReviews = () => (
  <div data-testid="page-assurance-reviews">
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <BackToDirectory />
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-5"><ShieldCheck className="w-3.5 h-3.5" /> Independent assurance</span>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-extrabold text-gray-900 leading-tight">Independent assurance reviews for care technology.</h1>
          <p className="text-lg text-gray-600 mt-5">Buyers trust independent evidence. We're building a paid assurance programme, structured reviews and reports that give care providers confidence and give great suppliers a credible mark of quality.</p>
          <ul className="mt-6 space-y-2">
            {['Structured, independent supplier reviews', 'Evidence buyers can rely on', 'A credible assurance mark for the best suppliers'].map((x) => (
              <li key={x} className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-4 h-4 text-[#00A79D]" /> {x}</li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 lg:sticky lg:top-24">
          <h2 className="font-heading text-xl font-semibold text-gray-900 mb-1">Register your interest</h2>
          <p className="text-sm text-gray-500 mb-4">For suppliers wanting to be reviewed, or providers who value independent evidence.</p>
          <CaptureForm source="assurance_review" interest="assurance review" cta="Register interest" testid="assurance-lead" />
        </div>
      </div>
    </section>
  </div>
);


// ---------------------------------------------------------------------------
// Data-driven procurement funnels (capture-only). Each posts to /public/leads
// with its own source, and surfaces in the admin Funnel Inbox "All leads" tab.
// ---------------------------------------------------------------------------
export const PROCUREMENT = {
  energy: {
    icon: Zap, source: 'procurement_energy', interest: 'energy procurement',
    title: 'Cut your energy costs without cutting corners.',
    subtitle: "Energy is a quietly enormous cost for care services. Tell us about your sites and usage and we'll help you benchmark tariffs and find a better deal with trusted brokers.",
    bullets: ['A fair comparison across care-friendly suppliers', 'Support with multi-site and half-hourly meters', 'No obligation, no hard sell'],
    cta: 'Request an energy review',
  },
  staffing: {
    icon: Users, source: 'procurement_staffing', interest: 'agency staffing',
    title: 'Fill shifts with quality agency staff, at a fair price.',
    subtitle: "Agency spend can spiral fast. We'll connect you with vetted staffing partners who understand social care, compliance and continuity of care.",
    bullets: ['Compliant, care-experienced agency workers', 'Transparent, fair framework rates', 'Cover for planned and urgent gaps'],
    cta: 'Request staffing support',
  },
  ppe: {
    icon: ShieldPlus, source: 'procurement_ppe', interest: 'PPE supply',
    title: 'Reliable PPE supply, sensible pricing.',
    subtitle: "Never get caught short again. We'll help you set up a dependable PPE supply with fair pricing and stock you can plan around.",
    bullets: ['Consistent, quality-assured supply', 'Fair, transparent pricing', 'Simple reordering across your sites'],
    cta: 'Request a PPE quote',
  },
  'it-telephony': {
    icon: Phone, source: 'procurement_it_telephony', interest: 'IT & telephony',
    title: 'Modern IT and telephony built for care.',
    subtitle: "From resilient broadband to cloud phone systems and support, we'll help you get IT and telephony that just works, without the jargon.",
    bullets: ['Resilient connectivity for every site', 'Cloud telephony and support built for care', 'Practical advice, not sales pressure'],
    cta: 'Request an IT review',
  },
  training: {
    icon: GraduationCap, source: 'procurement_training', interest: 'training & compliance',
    title: 'Training that keeps your team compliant and confident.',
    subtitle: "Care Certificate, mandatory and specialist training, delivered well. We'll help you find providers who fit your rota and your budget.",
    bullets: ['Care Certificate and mandatory training', 'Specialist and clinical courses', 'Tracking and compliance made simple'],
    cta: 'Request training options',
  },
  waste: {
    icon: Recycle, source: 'procurement_waste', interest: 'waste management',
    title: 'Compliant clinical and general waste, handled.',
    subtitle: "Waste is a compliance risk if it goes wrong. We'll connect you with providers who handle clinical, hazardous and general waste properly, at a fair price.",
    bullets: ['Fully compliant clinical waste handling', 'Reliable collections across your sites', 'Fair, transparent pricing'],
    cta: 'Request a waste review',
  },
};

export const ProcurementFunnel = () => {
  const { slug } = useParams();
  const cfg = PROCUREMENT[slug];
  if (!cfg) return <Navigate to="/procurement" replace />;
  const Icon = cfg.icon;
  return (
    <div data-testid={`page-procurement-${slug}`}>
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <Link to="/procurement" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#00A79D] mb-6" data-testid="proc-back-hub">
              <ArrowLeft className="w-4 h-4" /> All procurement services
            </Link>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-5"><Icon className="w-3.5 h-3.5" /> Procurement</span>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-extrabold text-gray-900 leading-tight">{cfg.title}</h1>
            <p className="text-lg text-gray-600 mt-5">{cfg.subtitle}</p>
            <ul className="mt-6 space-y-2">
              {cfg.bullets.map((x) => (
                <li key={x} className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-4 h-4 text-[#00A79D]" /> {x}</li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 lg:sticky lg:top-24">
            <h2 className="font-heading text-xl font-semibold text-gray-900 mb-1">Request a review</h2>
            <p className="text-sm text-gray-500 mb-4">We'll be in touch to understand your needs. No obligation.</p>
            <CaptureForm source={cfg.source} interest={cfg.interest} cta={cfg.cta} testid={`${slug}-lead`} />
            <Link to={`/procurement/${slug}/report`} className="mt-4 flex items-center justify-center gap-1.5 text-sm font-semibold text-[#00A79D] hover:text-[#00857D]" data-testid={`${slug}-report-cta`}>
              Or get a personalised report (£49) <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const HUB_ITEMS = [
  { to: '/procurement/energy', icon: Zap, title: 'Energy', desc: 'Benchmark tariffs and cut costs across your sites.' },
  { to: '/procurement/staffing', icon: Users, title: 'Agency staffing', desc: 'Quality, compliant agency cover at fair rates.' },
  { to: '/procurement/ppe', icon: ShieldPlus, title: 'PPE', desc: 'Dependable supply with sensible pricing.' },
  { to: '/procurement/it-telephony', icon: Phone, title: 'IT & telephony', desc: 'Resilient connectivity and cloud phone systems.' },
  { to: '/procurement/training', icon: GraduationCap, title: 'Training', desc: 'Compliant, well-delivered training for your team.' },
  { to: '/procurement/waste', icon: Recycle, title: 'Waste', desc: 'Compliant clinical and general waste handling.' },
  { to: '/procurement/insurance', icon: PoundSterling, title: 'Insurance', desc: 'A fair comparison across specialist care insurers.' },
];

export const ProcurementHub = () => (
  <div data-testid="page-procurement-hub">
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <BackToDirectory />
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-5"><PoundSterling className="w-3.5 h-3.5" /> Procurement</span>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-extrabold text-gray-900 leading-tight max-w-3xl">Better value on the things every care service buys.</h1>
        <p className="text-lg text-gray-600 mt-5 max-w-2xl">Beyond care technology, we help providers get a fair deal on the essentials, through trusted, independent partners. Tell us what you need and we'll do the legwork.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {HUB_ITEMS.map((h) => {
            const Icon = h.icon;
            return (
              <Link key={h.to} to={h.to} className="group border border-[#E5E7EB] rounded-lg p-5 hover:border-[#00A79D] hover:shadow-sm transition-all" data-testid={`proc-hub-${h.to.split('/').pop()}`}>
                <div className="w-10 h-10 rounded-md bg-[#00A79D]/10 text-[#00A79D] flex items-center justify-center mb-3"><Icon className="w-5 h-5" /></div>
                <p className="font-heading text-lg font-semibold text-gray-900">{h.title}</p>
                <p className="text-sm text-gray-600 mt-1">{h.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#00A79D] group-hover:gap-1.5 transition-all">Learn more <ArrowRight className="w-4 h-4" /></span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
    <PublicFaq id="procurement" heading="Procurement support FAQs"
      intro="How Care Tech Compare helps care providers get better value on the essentials - independently."
      faqs={[
        { q: 'What can you help me procure beyond technology?', a: 'As well as care technology, we help providers get a fair deal on the essentials every service buys - insurance, energy, waste, IT and connectivity, recruitment, training and more - through trusted, independent partners. Tell us what you need and we do the legwork to find suitable options.' },
        { q: 'Does it cost anything to use procurement support?', a: 'No. The service is free for care providers. We are funded by our partners, not by you, and we stay independent - our job is to save you time and money, not to push a particular supplier.' },
        { q: 'How does it actually work?', a: 'You tell us the category and your requirements. We shortlist relevant, vetted partners and either send you the information you need to decide or make a warm introduction to a small number of recommended providers - so you skip the cold research and comparison legwork.' },
        { q: 'Are the partners vetted?', a: 'Yes. We only work with partners we would recommend to a colleague, chosen for their track record with care services. You always remain in control of who you contract with - we simply narrow the field to trustworthy options.' },
      ]} />

  </div>
);
